import { useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Breadcrumb.scss';
import { capitalizeFirstLetter } from '../../stringUtils';
import { FarmDetailsType } from '../../../rtk/farmDetailsApi';
import { EntityInfo } from '@/rtk/entityInfoApi';

type Props = {
  farmDetails?: FarmDetailsType;
  entityInfo?: EntityInfo;
};

const Breadcrumb = ({ farmDetails, entityInfo }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const url = location.pathname;

  const [routes, setRoutes] = useState<ReactNode[]>();

  useEffect(() => {
    BreadcrumbRoutes();
  }, [url, entityInfo, farmDetails]);

  function BreadcrumbRoutes() {
    const splittedUrl = url.split('/');

    const isUserOnFarmDetailsPage = url.indexOf('/farms/') !== -1;
    const isUserOnEntityPage = splittedUrl.includes('entities');
    const isFarmsActive = isUserOnEntityPage || splittedUrl.includes('farms');

    if (isUserOnFarmDetailsPage && isFarmsActive) {
      splittedUrl.pop();
    }

    // Removes robot type from the url constant array in order to reduce changes in the code after
    splittedUrl.splice(3, 1);

    let i = 1;
    const breadcrumbRoutes: ReactNode[] = [];

    if (isFarmsActive) {
      breadcrumbRoutes.push(
        <li
          className={`breadcrumb-item`}
          key="farms"
          onClick={(event) => {
            event.preventDefault();
            navigate('/farms');
          }}
        >
          <a href="#">Farms</a>
        </li>,
        ...(isUserOnFarmDetailsPage
          ? [
              <li
                className="breadcrumb-item"
                key={i}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/farms/${farmDetails?.id}`);
                }}
              >
                <a href="#">
                  {capitalizeFirstLetter(farmDetails?.customerName)}
                </a>
              </li>,
            ]
          : []),
        ...(isUserOnEntityPage
          ? [
              <li
                className="breadcrumb-item"
                key={i}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/farms/${entityInfo?.farmId}`);
                }}
              >
                <a href="#">{capitalizeFirstLetter(entityInfo?.farmName)}</a>
              </li>,
            ]
          : []),
      );
    }

    for (i; i <= splittedUrl.length - 1; ) {
      if (splittedUrl[i] === 'entities') {
        const entityRoutes = [
          <li
            className="breadcrumb-item"
            key={splittedUrl[i]}
            aria-current="page"
            onClick={(event) => {
              event.preventDefault();

              if (entityInfo?.type === 'AstronautA5Next') {
                navigate(`/entities/${splittedUrl[2]}/astronaut/overview`);
              } else if (entityInfo?.type === 'AutomaticMilkFilter') {
                navigate(`/entities/${splittedUrl[2]}/amf/settings`);
              } else if (entityInfo?.type === 'Discovery') {
                navigate(`/entities/${splittedUrl[2]}/discovery/logs`);
              } else if (entityInfo?.type === 'Juno') {
                navigate(`/entities/${splittedUrl[2]}/juno/logs`);
              } else if (entityInfo?.type === 'FarmIT') {
                navigate(
                  `/entities/${splittedUrl[2]}/farmit/maintenance-portal`,
                );
              } else if (entityInfo?.type === 'NCapture') {
                navigate(
                  `/entities/${splittedUrl[2]}//n-capture/data-explorer`,
                );
              } else if (entityInfo?.type === 'AstronautMax') {
                navigate(
                  `/entities/${splittedUrl[2]}/astronaut-max/data-explorer`,
                );
              } else {
                navigate(`/entities/${splittedUrl[2]}/vector/data-explorer`);
              }
            }}
          >
            <a href="#">{capitalizeFirstLetter(entityInfo?.name)}</a>
          </li>,
        ];
        breadcrumbRoutes.push(...entityRoutes);
        i++;
      } else if (splittedUrl[i] !== 'farms') {
        // do not show Farms text in the breadcrumb for farms/ page
        if (splittedUrl[i] === 'settings') {
          breadcrumbRoutes.push(
            <li key={i + splittedUrl[i]} className="breadcrumb-item" />,
          );
          break;
        } else if (splittedUrl[i] === 'feedback') {
          break;
        }
        const isDomain =
          splittedUrl && splittedUrl[splittedUrl.length - 2] === 'domains';
        const isCanbus = splittedUrl && splittedUrl[i] === 'canbus';
        /** Get the penultimate page and add it to array */
        if (!isCanbus) {
          const otherRoutes = [
            <div
              className="breadcrumb-item"
              key={i + splittedUrl[i]}
              onClick={
                splittedUrl.length - 1 === i || isDomain
                  ? undefined
                  : (event) => {
                      event.preventDefault();
                      navigate(
                        `/entities/${splittedUrl[2]}/${splittedUrl[i - 2]}`,
                      );
                    }
              }
            >
              <a href="#">
                {splittedUrl[i] === 'domains'
                  ? 'Diagnostic'
                  : capitalizeFirstLetter(splittedUrl[i].replace(/-/g, ' '))}
              </a>
            </div>,
          ];
          breadcrumbRoutes.push(...otherRoutes);
        }
      }
      i++;
    }
    /** Update the state with all routes */
    setRoutes(breadcrumbRoutes);
  }

  return (
    <nav
      aria-label="breadcrumbsNav"
      style={{ display: 'flex', alignItems: 'center' }}
      translate="no"
    >
      <div className="breadcrumb" translate="no">
        {routes}
      </div>
    </nav>
  );
};

export default Breadcrumb;
